/**
 * Booking Engine Settings Custom Hooks
 *
 * Custom hooks wrapping TanStack Query for booking engine settings.
 * Follows the established pattern of encapsulating useQuery/useMutation in custom hooks.
 *
 * Pattern: Always wrap useQuery/useMutation in custom hooks rather than
 * calling them directly in components.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DepositType } from "../../pages/booking-engine/types/depositSettings";
import { api } from "../api";

/**
 * Get hotel branding settings
 */
export function useHotelBranding() {
  return useQuery({
    queryKey: ["booking-engine", "branding"],
    queryFn: api.getHotelBranding,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get deposit settings
 */
export function useDepositSettings() {
  return useQuery({
    queryKey: ["booking-engine", "deposit-settings"],
    queryFn: api.getDepositSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

interface UpdateBrandingData {
  primaryColor?: string;
  secondaryColor?: string;
  logo?: string;
  heroPhoto?: string;
  headerFont?: string;
  bodyFont?: string;
  name?: string;
  googleTagManagerId?: string;
}

interface UpdateDepositSettingsData {
  depositType?: DepositType;
  depositAmount?: number;
  depositPercentage?: number;
  includeTaxesAndFees?: boolean;
  collectAtBooking?: boolean;
  requireCreditCard?: boolean;
}

/**
 * Update hotel branding settings
 */
export function useUpdateHotelBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateBrandingData) => api.updateHotelBranding(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["booking-engine", "branding"],
      });
    },
  });
}

/**
 * Update deposit settings
 */
export function useUpdateDepositSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateDepositSettingsData) =>
      api.updateDepositSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["booking-engine", "deposit-settings"],
      });
    },
  });
}

/**
 * Get booking engine go-live status
 */
export function useBookingEngineGoLive() {
  return useQuery({
    queryKey: ["booking-engine", "go-live"],
    queryFn: api.getBookingEngineGoLive,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Request booking engine go-live (one-way action)
 */
export function useRequestBookingEngineGoLive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.requestBookingEngineGoLive,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["booking-engine", "go-live"],
      });
    },
  });
}

// Rate Plans Types
export type RefundPolicy =
  | "free_cancellation"
  | "free_until_arrival"
  | "flexible_24_hours"
  | "flexible_48_hours"
  | "flexible_3_days"
  | "flexible_4_days"
  | "flexible_7_days"
  | "flexible_10_days"
  | "flexible_14_days"
  | "strict_non_refundable";

export interface RatePlan {
  ratePlanId: string;
  ratePlanName: string;
  promoCode?: string;
  customPublicName?: string;
  description?: string;
  inclusions?: string;
  refundPolicy?: RefundPolicy;
  policies?: string;
}

export interface UpdateRatePlanData {
  customPublicName?: string;
  description?: string;
  inclusions?: string;
  refundPolicy?: RefundPolicy;
  policies?: string;
}

/**
 * Get rate plans (Cloudbeds only)
 */
export function useRatePlans() {
  return useQuery({
    queryKey: ["booking-engine", "rate-plans"],
    queryFn: api.getRatePlans,
  });
}

/**
 * Update a specific rate plan
 */
export function useUpdateRatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ratePlanId,
      data,
    }: {
      ratePlanId: string;
      data: UpdateRatePlanData;
    }) => api.updateRatePlan(ratePlanId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["booking-engine", "rate-plans"],
      });
    },
  });
}
